import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Modal, Skeleton, Tabs, Spin } from '@pankod/refine-antd'
import { ZoomIn } from 'lucide-react'

import { TCertificateTTEGetSubmissionResponse } from '../types'
import { getSVGStringFromUrl } from '@resources/lms-management/certificate-template/fabric/utils/canvas'

export const CertificatePreview: React.FC<{
  pengajuan: TCertificateTTEGetSubmissionResponse
}> = ({ pengajuan }) => {
  const [svgString, setSvgString] = useState({ front: '', back: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Modal states
  const [modalVisible, setModalVisible] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalContent, setModalContent] = useState('')
  const [isPreviewReady, setIsPreviewReady] = useState(false)

  const generateCertificatePreview = useCallback(async () => {
    if (!pengajuan.frontTemplateUrl && !pengajuan.backTemplateUrl) return

    setLoading(true)
    setError(false)
    try {
      const promises = [
        pengajuan.frontTemplateUrl
          ? getSVGStringFromUrl(pengajuan.frontTemplateUrl, undefined)
          : Promise.resolve(''),
      ]

      if (pengajuan.backTemplateUrl) {
        promises.push(getSVGStringFromUrl(pengajuan.backTemplateUrl, undefined))
      } else {
        promises.push(Promise.resolve(''))
      }

      const [front, back] = await Promise.all(promises)
      setSvgString({ front, back })
    } catch (error) {
      console.error('Error generating certificate preview:', error)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [pengajuan.frontTemplateUrl, pengajuan.backTemplateUrl])

  useEffect(() => {
    generateCertificatePreview()
  }, [generateCertificatePreview])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (modalVisible) {
      setIsPreviewReady(false)
      timer = setTimeout(() => {
        setIsPreviewReady(true)
      }, 350)
    }
    return () => clearTimeout(timer)
  }, [modalVisible])

  const handlePreviewClick = (title: string, content: string) => {
    if (!content) return
    setModalTitle(title)
    setModalContent(content)
    setModalVisible(true)
  }

  const handleModalClose = () => {
    setModalVisible(false)
    setModalContent('')
    setModalTitle('')
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Gagal memuat template sertifikat"
        showIcon
        className="mt-4"
      />
    )
  }

  const renderSvg = (title: string, svgContent: string) => {
    if (loading) {
      return (
        <div className="w-full flex items-center justify-center p-4 border rounded bg-gray-50">
          <Skeleton.Image active className="!w-[200px] !h-[282px]" />
        </div>
      )
    }

    if (!svgContent) {
      return (
        <div className="w-full p-4 border rounded bg-gray-50 text-center text-gray-400">
          Tidak ada template
        </div>
      )
    }

    return (
      <div
        className="relative border border-gray-300 rounded shadow-sm bg-white overflow-hidden cursor-pointer group mb-4"
        style={{ maxWidth: 500 }}
        onClick={() => handlePreviewClick(title, svgContent)}
      >
        <div
          className="[&>svg]:!w-full [&>svg]:!h-auto flex items-center justify-center bg-white transition-all duration-300 group-hover:scale-[1.02]"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex flex-col items-center text-white">
            <ZoomIn className="w-10 h-10 mb-2 opacity-90" />
            <span className="font-medium text-sm">Perbesar pratinjau</span>
          </div>
        </div>
      </div>
    )
  }

  const items = [
    {
      label: 'Halaman Depan',
      key: 'front',
      children: renderSvg('Halaman Depan', svgString.front),
    },
  ]

  if (pengajuan.backTemplateUrl) {
    items.push({
      label: 'Halaman Belakang',
      key: 'back',
      children: renderSvg('Halaman Belakang', svgString.back),
    })
  }

  return (
    <>
      <div className="certificate-preview-container">
        <Tabs items={items} />
      </div>

      <Modal
        open={modalVisible}
        title={`Pratinjau - ${modalTitle}`}
        onCancel={handleModalClose}
        footer={null}
        width="90vw"
        centered
        destroyOnClose
      >
        <div className="flex justify-center items-center min-h-[50vh]">
          {!isPreviewReady ? (
            <div className="w-full flex flex-col items-center justify-center h-full gap-3 text-gray-500">
              <Spin size="large" />
              <span>Memuat pratinjau...</span>
            </div>
          ) : (
            <div
              className="[&>svg]:!max-h-[80vh] [&>svg]:!w-auto w-fit bg-white border border-solid border-gray-300 rounded"
              dangerouslySetInnerHTML={{ __html: modalContent }}
            />
          )}
        </div>
      </Modal>
    </>
  )
}
